import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setInterval>[]} */
  #megaSliderIntervals = [];

  /** @type {(() => void)[]} */
  #megaHoverPreviewCleanups = [];

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    this.#initMegaSliders();
    this.#initMegaHoverPreviews();
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('click', this.#documentClickListener);
    this.addEventListener('click', this.#clickListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#documentClickListener);
    this.removeEventListener('click', this.#clickListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#clearMegaSliderIntervals();
    this.#clearMegaHoverPreviewCleanups();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  #documentClickListener = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#deactivate();
  };

  #clickListener = (event) => {
    if (!(event.target instanceof Element)) return;

    const sliderTrack = event.target.closest('[data-mega-slider-track]');
    if (sliderTrack instanceof HTMLElement && sliderTrack.dataset.dragged === 'true') {
      event.preventDefault();
      delete sliderTrack.dataset.dragged;
      return;
    }

    const link = event.target.closest('.menu-list__link[aria-haspopup="true"]');
    if (!(link instanceof HTMLElement) || !this.contains(link)) return;

    event.preventDefault();

    if (this.#state.activeItem === link) {
      this.#deactivate(link);
      return;
    }

    this.activate(event);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  #initMegaSliders() {
    this.#clearMegaSliderIntervals();

    this.querySelectorAll('[data-mega-slider]').forEach((slider) => {
      if (!(slider instanceof HTMLElement)) return;
      const track = slider.querySelector('[data-mega-slider-track]');
      if (!(track instanceof HTMLElement) || track.children.length <= 1) return;

      const durationSeconds = Number(slider.dataset.duration) || 6;
      const interval = window.setInterval(() => {
        const menuItem = slider.closest('.menu-list__list-item');
        const isOpen = menuItem?.hasAttribute('data-click-open');
        if (!isOpen || track.dataset.dragging === 'true') return;
        if (track.clientWidth <= 0) return;
        this.#scrollMegaSlider(track, 1);
      }, durationSeconds * 1000);

      this.#megaSliderIntervals.push(interval);
      this.#bindMegaSliderDrag(track);
    });
  }

  #clearMegaSliderIntervals() {
    this.#megaSliderIntervals.forEach((interval) => window.clearInterval(interval));
    this.#megaSliderIntervals = [];
  }

  /**
   * @param {HTMLElement} track
   * @param {number} direction
   */
  #scrollMegaSlider(track, direction) {
    const slideWidth = track.clientWidth;
    if (slideWidth <= 0) return;

    const maxScrollLeft = Math.max(0, track.scrollWidth - slideWidth);
    if (maxScrollLeft <= 2) return;

    const atEnd = track.scrollLeft >= maxScrollLeft - 2;
    const nextLeft = direction > 0 && atEnd ? 0 : track.scrollLeft + slideWidth * direction;

    track.scrollTo({ left: nextLeft, behavior: 'smooth' });
  }

  /**
   * Reset open mega-menu sliders to the first slide.
   * @param {HTMLElement | null} [submenu]
   */
  #resetMegaSliders(submenu) {
    const root = submenu instanceof HTMLElement ? submenu : this;
    root.querySelectorAll('[data-mega-slider-track]').forEach((track) => {
      if (!(track instanceof HTMLElement) || track.children.length <= 1) return;
      track.scrollTo({ left: 0, behavior: 'auto' });
    });
  }

  /**
   * @param {HTMLElement} track
   */
  #bindMegaSliderDrag(track) {
    if (track.dataset.megaSliderBound === 'true') return;
    track.dataset.megaSliderBound = 'true';

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;

    track.addEventListener('pointerdown', (event) => {
      if (!(event instanceof PointerEvent)) return;
      isDragging = true;
      moved = false;
      startX = event.clientX;
      startScrollLeft = track.scrollLeft;
      track.dataset.dragging = 'true';
      track.setPointerCapture(event.pointerId);
    });

    track.addEventListener('pointermove', (event) => {
      if (!isDragging) return;
      const deltaX = event.clientX - startX;
      if (Math.abs(deltaX) > 6) moved = true;
      track.scrollLeft = startScrollLeft - deltaX;
    });

    const endDrag = (event) => {
      if (!isDragging) return;
      isDragging = false;
      delete track.dataset.dragging;
      if (moved) track.dataset.dragged = 'true';

      const slideWidth = track.clientWidth || 1;
      const targetIndex = Math.round(track.scrollLeft / slideWidth);
      track.scrollTo({ left: targetIndex * slideWidth, behavior: 'smooth' });

      if (event instanceof PointerEvent && track.hasPointerCapture(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);
  }

  #initMegaHoverPreviews() {
    this.querySelectorAll('[data-mega-hover-preview]').forEach((preview) => {
      if (!(preview instanceof HTMLElement)) return;

      const menu = preview.closest('.ananas-mega-menu');
      const image = preview.querySelector('.ananas-mega-menu__preview-image');
      const title = preview.querySelector('[data-mega-hover-preview-title]');
      const link = preview.querySelector('[data-mega-hover-preview-link]');
      if (!(menu instanceof HTMLElement) || !(image instanceof HTMLImageElement)) return;

      const hoverLinks = [...menu.querySelectorAll('[data-mega-hover-image]')];
      hoverLinks.forEach((hoverLink) => {
        if (!(hoverLink instanceof HTMLElement)) return;

        const updatePreview = () => {
          const nextImage = hoverLink.dataset.megaHoverImage;
          if (!nextImage) return;

          image.src = nextImage;
          image.removeAttribute('srcset');
          image.alt = hoverLink.dataset.megaHoverTitle || hoverLink.textContent?.trim() || '';

          if (title) {
            title.textContent = hoverLink.dataset.megaHoverTitle || hoverLink.textContent?.trim() || '';
          }
          if (link instanceof HTMLAnchorElement && hoverLink.dataset.megaHoverLink) {
            link.href = hoverLink.dataset.megaHoverLink;
          }
        };

        hoverLink.addEventListener('pointerenter', updatePreview);
        hoverLink.addEventListener('focus', updatePreview);
        this.#megaHoverPreviewCleanups.push(() => {
          hoverLink.removeEventListener('pointerenter', updatePreview);
          hoverLink.removeEventListener('focus', updatePreview);
        });
      });
    });
  }

  #clearMegaHoverPreviewCleanups() {
    this.#megaHoverPreviewCleanups.forEach((cleanup) => cleanup());
    this.#megaHoverPreviewCleanups = [];
  }

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {Event} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;
    if (event.type !== 'click') return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const itemContainer = item.closest('.menu-list__list-item');
    const isDefaultSlot = itemContainer?.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      previouslyActiveItem.closest('.menu-list__list-item')?.removeAttribute('data-click-open');
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    item.closest('.menu-list__list-item')?.setAttribute('data-click-open', 'true');

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
      this.#resetMegaSliders(submenu);

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.closest('.menu-list__list-item')?.removeAttribute('data-click-open');

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) {
    return element instanceof HTMLElement ? element : null;
  }

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]') ?? null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
